import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ThemeContext } from '../context/ThemeContext';
import { AuthContext } from '../context/AuthContext';
import { useApi } from '../hooks/useApi';
import { formatMoney } from '../utils/money';
import {
  AGING_OPTIONS,
  agingLabel,
  agingTone,
  cobranzaWhatsappText,
  gestionesOf,
  whatsappHref,
} from '../utils/cobranzas';
import CobranzaGestionesModal from '../components/CobranzaGestionesModal';
import CobranzaClientRanking from '../components/CobranzaClientRanking';
import CollectionCycleMonthCard from '../components/CollectionCycleMonthCard';
import MpCobrosReconcilePanel from '../components/MpCobrosReconcilePanel';
import ClientRiskAlertPanel from '../components/ClientRiskAlertPanel';
import { currentMonthRange, downloadCollectionExport } from '../utils/collectionExport';
import { canAccessCobranzasEssential, canAccessProfesionalFeatures } from '../utils/profesionalAccess';
import { isOperationalWriteDisabled } from '../utils/writeAccess';
import { scoreBadgeClasses, scoreTone } from '../utils/collectionScore';
import { sortHubRowsByPriority } from '../utils/cobrarPriority';
import { whatsappOptionsFromPrefs } from '../utils/whatsappTemplates';
import { usePageFeedback } from '../hooks/usePageFeedback';
import PageFeedbackBlock from '../components/PageFeedbackBlock';
import PlanUpgradeCard from '../components/PlanUpgradeCard';
import ContextualAssistantStrip from '../components/ContextualAssistantStrip';
import {
  FiMail,
  FiRefreshCw,
  FiExternalLink,
  FiMessageCircle,
  FiCreditCard,
  FiArrowRight,
  FiDollarSign,
  FiAlertTriangle,
  FiClock,
  FiSearch,
  FiSettings,
  FiZap,
  FiCheckCircle,
  FiBarChart2,
  FiDownload,
} from 'react-icons/fi';

const FILTER_OPTIONS = [
  ...AGING_OPTIONS,
  { id: 'vencidas', label: 'Todas vencidas' },
];

function formatFecha(iso) {
  if (!iso) return '—';
  const s = String(iso).slice(0, 10);
  const [y, m, d] = s.split('-').map(Number);
  if (!y || !m || !d) return s;
  return new Date(y, m - 1, d).toLocaleDateString('es-AR', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function mapGestionesToRow(row, gestiones) {
  const last = gestiones[0] || null;
  return {
    ...row,
    gestiones,
    gestionesCount: gestiones.length,
    lastGestion: last
      ? {
        id: last.id,
        tipo: last.tipo,
        fecha: last.fecha || last.createdAt || null,
        texto: last.texto || '',
        promesaFecha: last.promesaFecha || null,
        automatico: Boolean(last.automatico),
      }
      : null,
  };
}

function matchesRowAging(row, filter) {
  if (!filter || filter === 'todas') return true;
  if (filter === 'vencidas') return Boolean(row.aging && row.aging !== 'vigente');
  return row.aging === filter;
}

function KpiCard({ label, value, hint, color, modoOscuro, highlight, icon: Icon }) {
  return (
    <div
      className={`px-4 py-3.5 ${
        highlight
          ? modoOscuro ? 'bg-[#1F80FF]/10' : 'bg-[#1F80FF]/5'
          : modoOscuro ? 'bg-gray-900' : 'bg-white'
      }`}
    >
      <div className="flex items-center justify-between gap-2 mb-1">
        <div className="text-[10px] font-semibold tracking-wide uppercase text-gray-400">{label}</div>
        {Icon && (
          <span className={`p-1.5 rounded-lg ${modoOscuro ? 'bg-gray-800 text-gray-400' : 'bg-gray-100 text-gray-500'}`}>
            <Icon className="w-3.5 h-3.5" />
          </span>
        )}
      </div>
      <div className={`text-lg sm:text-xl font-semibold tabular-nums ${color}`}>{value}</div>
      {hint && <div className="text-[11px] text-gray-400 mt-0.5">{hint}</div>}
    </div>
  );
}

function Skeleton({ box, modoOscuro }) {
  const pulse = modoOscuro ? 'bg-gray-800 animate-pulse' : 'bg-gray-200 animate-pulse';
  return (
    <div className="space-y-4">
      <div className={`rounded-2xl border ${box} p-4 space-y-3`}>
        <div className={`h-4 w-40 rounded ${pulse}`} />
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className={`h-14 rounded-xl ${pulse}`} />
        ))}
      </div>
      <div className={`rounded-2xl border overflow-hidden ${box}`}>
        <div className={`grid grid-cols-3 gap-px ${modoOscuro ? 'bg-gray-800' : 'bg-gray-100'}`}>
          {[1, 2, 3].map((i) => (
            <div key={i} className={`h-16 ${modoOscuro ? 'bg-gray-900' : 'bg-white'} p-4`}>
              <div className={`h-3 w-16 rounded mb-2 ${pulse}`} />
              <div className={`h-5 w-20 rounded ${pulse}`} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ActionBtn({ onClick, disabled, title, icon: Icon, label, shortLabel, modoOscuro, primary, className = '' }) {
  const base = modoOscuro
    ? 'border-gray-600 text-gray-200 hover:bg-gray-800 disabled:opacity-40'
    : 'border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-40';
  const primaryCls = 'bg-[#1F80FF] text-white border-[#1F80FF] hover:bg-[#004AAD] disabled:opacity-50';
  const displayLabel = shortLabel || label;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`inline-flex min-h-[44px] items-center justify-center gap-1.5 px-3 py-2 rounded-xl border text-xs font-semibold transition-colors ${
        primary ? primaryCls : base
      } ${className}`}
    >
      <Icon className="w-4 h-4 shrink-0" />
      <span className="truncate">
        <span className="sm:hidden">{displayLabel}</span>
        <span className="hidden sm:inline">{label}</span>
      </span>
    </button>
  );
}

function InvoiceRowActions({
  row,
  hasProfesional,
  hasCobranzasEssential,
  actionId,
  modoOscuro,
  onRecordatorio,
  onPaymentLink,
  whatsappOptions,
  onOpenGestiones,
  onLogGestion,
  readOnly = false,
}) {
  const facturaStub = {
    numero: row.numero,
    razonSocial: row.cliente,
    total: row.total,
    moneda: row.moneda,
    vencimiento: row.vencimiento,
    fecha: row.fecha,
    telefono: row.telefono,
    pagos: row.pagado > 0 ? [{ monto: row.pagado }] : [],
  };
  const wa = whatsappHref(row.telefono, cobranzaWhatsappText(facturaStub, whatsappOptions));
  const busy = actionId === row.id;

  const openPaymentLink = () => {
    if (row.paymentLink) {
      window.open(row.paymentLink, '_blank', 'noopener,noreferrer');
      return;
    }
    onPaymentLink(row);
  };

  const actions = [
    hasCobranzasEssential && {
      key: 'hist',
      icon: FiClock,
      label: row.gestionesCount ? `Historial (${row.gestionesCount})` : 'Historial',
      shortLabel: row.gestionesCount ? `Hist. (${row.gestionesCount})` : 'Historial',
      title: 'Ver y registrar gestiones',
      onClick: () => onOpenGestiones(row),
    },
    wa && {
      key: 'wa',
      icon: FiMessageCircle,
      label: 'WhatsApp',
      shortLabel: 'WhatsApp',
      title: 'Enviar WhatsApp',
      onClick: () => {
        window.open(wa, '_blank', 'noopener,noreferrer');
        if (hasCobranzasEssential && onLogGestion && !readOnly) {
          onLogGestion(row.id, { tipo: 'whatsapp', texto: 'Mensaje de WhatsApp abierto' }, { silent: true }).catch(() => {});
        }
      },
    },
    hasProfesional && !readOnly && row.hasEmail && {
      key: 'mail',
      icon: FiMail,
      label: 'Email',
      shortLabel: 'Email',
      title: 'Recordatorio por email',
      disabled: busy,
      onClick: () => onRecordatorio(row.id),
    },
    hasProfesional && !readOnly && row.moneda === 'ARS' && {
      key: 'paylink',
      icon: row.paymentLink ? FiExternalLink : FiCreditCard,
      label: row.paymentLink ? 'Link de pago' : 'Cobro sin link',
      shortLabel: row.paymentLink ? 'Abrir link' : 'Configurar',
      title: row.paymentLink
        ? 'Abrir tu link de pago (configurado en Cuenta)'
        : 'Configurá alias, CBU o link mpago.la en Cuenta → Datos para cobrar',
      disabled: busy,
      onClick: openPaymentLink,
      primary: Boolean(row.paymentLink),
    },
  ].filter(Boolean);

  if (!actions.length) return null;

  return (
    <div className={`grid gap-2 sm:flex sm:flex-wrap sm:justify-end ${
      actions.length === 1 ? 'grid-cols-1' : actions.length === 2 ? 'grid-cols-2' : 'grid-cols-2 sm:grid-cols-3'
    }`}>
      {actions.map((action) => (
        <ActionBtn
          key={action.key}
          icon={action.icon}
          label={action.label}
          shortLabel={action.shortLabel}
          title={action.title}
          disabled={action.disabled}
          onClick={action.onClick}
          modoOscuro={modoOscuro}
          primary={action.primary}
          className="w-full sm:w-auto"
        />
      ))}
    </div>
  );
}

function InvoiceCard({
  row,
  hasProfesional,
  hasCobranzasEssential,
  actionId,
  modoOscuro,
  textPri,
  textMuted,
  divide,
  onRecordatorio,
  onPaymentLink,
  whatsappOptions,
  onOpenGestiones,
  onLogGestion,
  readOnly = false,
}) {
  const facturaStub = {
    numero: row.numero,
    razonSocial: row.cliente,
    total: row.total,
    moneda: row.moneda,
    vencimiento: row.vencimiento,
    fecha: row.fecha,
    telefono: row.telefono,
    pagos: row.pagado > 0 ? [{ monto: row.pagado }] : [],
  };
  const aging = agingLabel(facturaStub);
  const partial = row.pagado > 0 && row.saldo > 0.009;

  return (
    <article className={`p-4 space-y-3 ${divide}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className={`text-sm font-semibold ${textPri}`}>N°{row.numero}</span>
            {partial && (
              <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${modoOscuro ? 'bg-sky-900/40 text-sky-200' : 'bg-sky-50 text-sky-800'}`}>
                Parcial
              </span>
            )}
            {aging && (
              <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold ${agingTone(facturaStub, modoOscuro)}`}>
                {aging}
              </span>
            )}
            {hasProfesional && row.score != null && (
              <span className={`inline-flex px-2 py-0.5 rounded-lg text-[10px] font-bold ${scoreBadgeClasses(scoreTone(row.score), modoOscuro)}`}>
                Score {row.score}
              </span>
            )}
          </div>
          <p className={`text-sm font-medium mt-1 ${textPri}`}>{row.cliente}</p>
          <p className={`text-xs mt-0.5 ${textMuted}`}>
            Vence {formatFecha(row.vencimiento || row.fecha)}
          </p>
          {row.email && (
            <p className={`text-xs mt-0.5 truncate ${textMuted}`}>{row.email}</p>
          )}
          {hasCobranzasEssential && row.lastGestion && (
            <button
              type="button"
              onClick={() => onOpenGestiones(row)}
              className={`text-[11px] mt-1 text-left hover:underline ${modoOscuro ? 'text-[#5CA3FF]' : 'text-[#1F80FF]'}`}
            >
              Última: {row.lastGestion.tipo} · {formatFecha(row.lastGestion.fecha)}
            </button>
          )}
          {partial && (
            <p className={`text-[11px] mt-1 ${textMuted}`}>
              Pagado {formatMoney(row.pagado, row.moneda || 'ARS')}
            </p>
          )}
        </div>
        <div className="text-right shrink-0 pl-2">
          <div className={`text-lg font-bold tabular-nums leading-tight ${textPri}`}>
            {formatMoney(row.saldo, row.moneda || 'ARS')}
          </div>
          <p className={`text-[10px] uppercase tracking-wide mt-1 ${textMuted}`}>Saldo</p>
        </div>
      </div>
      <InvoiceRowActions
        row={row}
        hasProfesional={hasProfesional}
        hasCobranzasEssential={hasCobranzasEssential}
        actionId={actionId}
        modoOscuro={modoOscuro}
        onRecordatorio={onRecordatorio}
        onPaymentLink={onPaymentLink}
        whatsappOptions={whatsappOptions}
        onOpenGestiones={onOpenGestiones}
        onLogGestion={onLogGestion}
        readOnly={readOnly}
      />
    </article>
  );
}

export default function Cobranzas() {
  const { modoOscuro } = useContext(ThemeContext);
  const { user } = useContext(AuthContext);
  const { secureFetch } = useApi();
  const navigate = useNavigate();
  const hasProfesional = canAccessProfesionalFeatures(user);
  const hasCobranzasEssential = canAccessCobranzasEssential(user);
  const readOnly = isOperationalWriteDisabled(user);

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const {
    success,
    error,
    clearFeedback,
    clearError,
    setError,
    reportSuccess,
    reportError,
  } = usePageFeedback();
  const [hub, setHub] = useState(null);
  const [actionId, setActionId] = useState(null);
  const [agingFilter, setAgingFilter] = useState('todas');
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('prioridad');
  const [lastUpdated, setLastUpdated] = useState(null);
  const [gestionRow, setGestionRow] = useState(null);
  const [savingGestion, setSavingGestion] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [searchParams] = useSearchParams();

  const box = modoOscuro ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200';
  const textPri = modoOscuro ? 'text-white' : 'text-gray-900';
  const textMuted = modoOscuro ? 'text-gray-400' : 'text-gray-500';
  const divide = modoOscuro ? 'border-gray-700' : 'border-gray-200';
  const btnOut = modoOscuro ? 'border-gray-600 text-white hover:bg-gray-800' : 'border-gray-300 text-gray-800 hover:bg-gray-50';
  const inputClass = modoOscuro
    ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-500'
    : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400';

  const green = modoOscuro ? 'text-green-300' : 'text-green-700';
  const red = modoOscuro ? 'text-red-300' : 'text-red-700';
  const amber = modoOscuro ? 'text-amber-300' : 'text-amber-700';

  const fetchHub = useCallback(async (silent = false) => {
    if (silent) setRefreshing(true);
    else setLoading(true);
    if (!silent) clearError();
    try {
      const res = await secureFetch('/api/cobranzas/hub');
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Error al cargar cobranzas');
      setHub(data);
      setLastUpdated(new Date());
    } catch (err) {
      if (silent) setError(err.message);
      else reportError(err.message || 'Error al cargar cobranzas');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [secureFetch, clearError, setError, reportError]);

  useEffect(() => {
    fetchHub();
  }, [fetchHub]);

  useEffect(() => {
    const q = searchParams.get('q');
    if (q) setSearch(q);
  }, [searchParams]);

  const handleRecordatorio = async (id) => {
    if (readOnly) return;
    setActionId(id);
    clearFeedback();
    try {
      const res = await secureFetch(`/api/invoices/${id}/reminder`, { method: 'POST' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Error al enviar el recordatorio');
      reportSuccess(data.message || 'Recordatorio de pago enviado correctamente.');
    } catch (err) {
      reportError(err.message || 'Error al enviar el recordatorio');
    } finally {
      setActionId(null);
    }
  };

  const applyGestionesToHub = useCallback((invoiceId, invoiceData) => {
    const gestiones = gestionesOf(invoiceData);
    setHub((prev) => {
      if (!prev?.rows) return prev;
      return {
        ...prev,
        rows: prev.rows.map((row) => (
          row.id === invoiceId ? mapGestionesToRow(row, gestiones) : row
        )),
      };
    });
    setGestionRow((prev) => (
      prev?.id === invoiceId ? mapGestionesToRow(prev, gestiones) : prev
    ));
  }, []);

  const handleAddGestion = async (invoiceId, payload, { silent = false } = {}) => {
    if (readOnly) return;
    if (!silent) setSavingGestion(true);
    try {
      const res = await secureFetch(`/api/invoices/${invoiceId}/gestiones`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Error al guardar la gestión');
      applyGestionesToHub(invoiceId, data);
      if (payload.tipo === 'promesa') fetchHub(true);
      if (!silent) reportSuccess('Gestión registrada.');
      return data;
    } catch (err) {
      if (!silent) reportError(err.message || 'Error al guardar la gestión');
      throw err;
    } finally {
      if (!silent) setSavingGestion(false);
    }
  };

  const handleExportPdf = async () => {
    setExportingPdf(true);
    clearFeedback();
    try {
      const range = currentMonthRange();
      await downloadCollectionExport(secureFetch, { ...range, format: 'pdf' });
      reportSuccess('PDF de cobranza descargado.');
    } catch (err) {
      reportError(err.message || 'Error al exportar PDF');
    } finally {
      setExportingPdf(false);
    }
  };

  const handlePaymentLink = async (row) => {
    if (readOnly) return;
    if (row.paymentLink) {
      window.open(row.paymentLink, '_blank', 'noopener,noreferrer');
      return;
    }
    if (!hub?.collectionPrefs?.configured) {
      navigate('/cuenta?tab=cobro');
      return;
    }
    setActionId(row.id);
    clearFeedback();
    try {
      const res = await secureFetch(`/api/invoices/${row.id}/payment-link`, { method: 'POST' });
      const data = await res.json();
      if (!res.ok) {
        if (data.code === 'PAYMENT_SETUP_REQUIRED') {
          reportError(data.message || 'Configurá tus datos de cobro en Cuenta.');
          return;
        }
        throw new Error(data.message || 'Error al obtener link de pago');
      }
      if (data.transferOnly) {
        const saldoTxt = Number(row.saldo || data.amount || 0).toLocaleString('es-AR', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
        const lines = [
          `Hola ${row.cliente}, datos para pagar el comprobante #${row.numero} (${row.moneda || 'ARS'} ${saldoTxt}):`,
          data.paymentAlias ? `Alias: ${data.paymentAlias}` : null,
          data.paymentCbu ? `CBU/CVU: ${data.paymentCbu}` : null,
          data.paymentHolderName ? `Titular: ${data.paymentHolderName}` : null,
        ].filter(Boolean);
        const text = lines.join('\n');
        const wa = whatsappHref(row.telefono, text);
        if (wa) {
          window.open(wa, '_blank', 'noopener,noreferrer');
          reportSuccess('Abrimos WhatsApp con alias/CBU para cobrar.');
        } else {
          reportSuccess(`${text} (copiá y enviá al cliente)`);
        }
        return;
      }

      if (data.initPoint || data.paymentLink) {
        const url = data.initPoint || data.paymentLink;
        window.open(url, '_blank', 'noopener,noreferrer');
        reportSuccess(
          data.source === 'user' ? 'Abrimos tu link de pago configurado.' : 'Link de pago generado.',
        );
        fetchHub(true);
      }
    } catch (err) {
      reportError(err.message || 'Error al obtener link de pago');
    } finally {
      setActionId(null);
    }
  };

  const filteredRows = useMemo(() => {
    if (!hub?.rows) return [];
    const q = search.trim().toLowerCase();
    let list = hub.rows.filter((row) => matchesRowAging(row, agingFilter));
    if (q) {
      list = list.filter((row) =>
        String(row.numero).includes(q)
        || row.cliente?.toLowerCase().includes(q)
        || row.email?.toLowerCase().includes(q));
    }
    if (sortBy === 'saldo') {
      list = [...list].sort((a, b) => b.saldoArs - a.saldoArs);
    } else if (sortBy === 'cliente') {
      list = [...list].sort((a, b) => String(a.cliente).localeCompare(String(b.cliente), 'es'));
    } else if (sortBy === 'score' && hasProfesional) {
      list = [...list].sort((a, b) => (a.score ?? 100) - (b.score ?? 100));
    } else if (sortBy === 'prioridad') {
      list = sortHubRowsByPriority(list, {
        promesas: hub?.promesas || [],
        profesional: hasProfesional,
      });
    }
    return list;
  }, [hub?.rows, hub?.promesas, agingFilter, search, sortBy, hasProfesional]);

  const promesasVencidas = useMemo(
    () => (hub?.promesas || []).filter((p) => p.vencida),
    [hub?.promesas],
  );

  const summary = hub?.summary;
  const prefs = hub?.collectionPrefs;
  const whatsappOptions = useMemo(
    () => whatsappOptionsFromPrefs(prefs, user),
    [prefs, user],
  );
  const allClear = !loading && hub && !hub.rows?.length;

  return (
    <div className={modoOscuro ? 'min-h-[calc(100dvh-4rem)] bg-black text-white' : 'min-h-[calc(100dvh-4rem)] bg-[#f5f5f7] text-gray-900'}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 sm:py-6 space-y-4 sm:space-y-5">
        {/* Header */}
        <header className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-start sm:justify-between sm:gap-4">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-1">
              {summary?.secuenciasActivas && (
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                  modoOscuro ? 'bg-green-900/40 text-green-200' : 'bg-green-50 text-green-800'
                }`}>
                  <FiZap className="w-3 h-3" />
                  Secuencias activas
                </span>
              )}
              {(summary?.promesasVencidas ?? 0) > 0 && (
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                  modoOscuro ? 'bg-red-900/40 text-red-200' : 'bg-red-50 text-red-700'
                }`}>
                  <FiAlertTriangle className="w-3 h-3" />
                  {summary.promesasVencidas} promesa(s) vencida(s)
                </span>
              )}
            </div>
            <h1 className={`text-xl sm:text-3xl font-semibold ${textPri}`}>Cobrar</h1>
            {summary && (
              <p className={`text-sm sm:text-base mt-1 font-medium tabular-nums ${textPri}`}>
                {formatMoney(summary.totalPendienteArs, 'ARS')} pendientes
                <span className={`font-normal ${textMuted}`}>
                  {' · '}{summary.impagas} impaga(s)
                  {summary.vencidas > 0 && (
                    <span className={red}> · {summary.vencidas} vencida(s)</span>
                  )}
                </span>
              </p>
            )}
            <p className={`text-xs mt-1 ${textMuted}`}>
              Priorizá impagas y ejecutá acciones de cobro.
              {lastUpdated && (
                <span className="ml-1">
                  · Actualizado {lastUpdated.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })}
                </span>
              )}
            </p>
          </div>
          <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:items-center sm:shrink-0">
            <Link
              to="/reportes?tab=cobranzas"
              className={`inline-flex min-h-[44px] items-center justify-center gap-1.5 rounded-xl border px-3 py-2 text-xs sm:text-sm font-semibold text-[#1F80FF] hover:underline sm:border-transparent ${btnOut} sm:bg-transparent`}
            >
              <FiBarChart2 className="w-4 h-4 shrink-0" />
              <span className="truncate">Análisis</span>
              <FiArrowRight className="w-3.5 h-3.5 shrink-0 hidden sm:block" />
            </Link>
            {hasProfesional && (
              <button
                type="button"
                onClick={handleExportPdf}
                disabled={exportingPdf || loading}
                className={`inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl border px-3 py-2 text-xs sm:text-sm font-semibold disabled:opacity-50 ${btnOut}`}
              >
                <FiDownload className="w-4 h-4 shrink-0" />
                {exportingPdf ? 'Exportando…' : 'PDF'}
              </button>
            )}
            <button
              type="button"
              onClick={() => fetchHub(true)}
              disabled={loading || refreshing}
              className={`inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl border px-3 py-2 text-xs sm:text-sm font-semibold disabled:opacity-50 ${btnOut}`}
            >
              <FiRefreshCw className={`w-4 h-4 shrink-0 ${refreshing ? 'animate-spin' : ''}`} />
              Actualizar
            </button>
          </div>
        </header>

        <PageFeedbackBlock success={success} error={error} />

        {!hasProfesional && (
          <PlanUpgradeCard
            compact
            description="Link de pago propio, recordatorios por email, score y secuencias automáticas están en plan Profesional+."
            planLabel="Profesional"
            modoOscuro={modoOscuro}
          />
        )}

        {hasProfesional && (
          <ContextualAssistantStrip
            contextId="cobranzas"
            secureFetch={secureFetch}
            hasProfesional={hasProfesional}
            modoOscuro={modoOscuro}
            textSec={textMuted}
            textMuted={textMuted}
          />
        )}

        {hasCobranzasEssential && hub?.collectionPrefs && !hub.collectionPrefs.configured && (
          <div
            className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 rounded-xl border px-3 py-2.5 text-xs ${
              modoOscuro ? 'border-amber-800/50 bg-amber-950/20' : 'border-amber-200 bg-amber-50/90'
            }`}
          >
            <p className={modoOscuro ? 'text-amber-100' : 'text-amber-950'}>
              <span className="font-semibold">Configurá medios de cobro</span>
              <span className={modoOscuro ? 'text-amber-200/80' : 'text-amber-800'}>
                {' '}— alias, CBU o link para WhatsApp y pagos.
              </span>
            </p>
            <Link
              to="/cuenta?tab=cobro"
              className="shrink-0 font-semibold text-[#1F80FF] hover:underline"
            >
              Cuenta → Cobrar
            </Link>
          </div>
        )}

        {hasCobranzasEssential && (
          <CollectionCycleMonthCard
            secureFetch={secureFetch}
            hasProfesional={hasProfesional}
            modoOscuro={modoOscuro}
            box={box}
            textPri={textPri}
            textSec={textMuted}
            textMuted={textMuted}
            compact
            hubSummary={hub?.summary}
          />
        )}

        {hasProfesional && (
          <MpCobrosReconcilePanel
            secureFetch={secureFetch}
            modoOscuro={modoOscuro}
            box={box}
            textPri={textPri}
            textMuted={textMuted}
            onApplied={() => fetchHub(true)}
          />
        )}

        {hasProfesional && hub?.atRiskClients?.length > 0 && (
          <ClientRiskAlertPanel
            clients={hub.atRiskClients}
            lookbackDays={hub.clientRiskLookbackDays}
            modoOscuro={modoOscuro}
            box={box}
            textPri={textPri}
            textMuted={textMuted}
            onSelectClient={(cliente) => setSearch(cliente)}
          />
        )}

        {loading && !hub ? (
          <Skeleton box={box} modoOscuro={modoOscuro} />
        ) : allClear ? (
          <div className={`rounded-2xl border p-10 text-center ${box}`}>
            <div className={`inline-flex p-4 rounded-full mb-4 ${modoOscuro ? 'bg-green-900/30 text-green-300' : 'bg-green-50 text-green-600'}`}>
              <FiCheckCircle className="w-10 h-10" />
            </div>
            <h2 className={`text-lg font-semibold ${textPri}`}>¡Al día con las cobranzas!</h2>
            <p className={`text-sm mt-2 max-w-md mx-auto ${textMuted}`}>
              No tenés comprobantes pendientes. Cuando registres ventas impagas, aparecerán acá con aging, score y acciones rápidas.
            </p>
            <Link
              to="/facturas"
              className="inline-flex items-center gap-2 mt-5 px-4 py-2 rounded-xl text-sm font-semibold bg-[#1F80FF] text-white hover:bg-[#004AAD]"
            >
              Ir a ventas
              <FiArrowRight className="w-4 h-4" />
            </Link>
          </div>
        ) : (
          <div className="flex flex-col gap-4 sm:gap-5">
            {summary && (
              <section className={`order-1 rounded-2xl border overflow-hidden lg:order-2 ${box}`}>
                <div className={`grid grid-cols-1 sm:grid-cols-3 gap-px ${modoOscuro ? 'bg-gray-800' : 'bg-gray-100'}`}>
                  <KpiCard
                    label="Pendiente total"
                    value={formatMoney(summary.totalPendienteArs, 'ARS')}
                    hint={`${summary.impagas} comprobante(s) impago(s)`}
                    color={summary.totalPendienteArs > 0 ? amber : green}
                    modoOscuro={modoOscuro}
                    highlight
                    icon={FiDollarSign}
                  />
                  <KpiCard
                    label="Vencidas"
                    value={summary.vencidas}
                    hint="Fuera de plazo"
                    color={summary.vencidas > 0 ? red : green}
                    modoOscuro={modoOscuro}
                    icon={FiClock}
                  />
                  <KpiCard
                    label="Promesas"
                    value={summary.promesasVencidas > 0 ? `${summary.promesasVencidas} vencida(s)` : summary.promesasProximas || 0}
                    hint={summary.promesasVencidas > 0 ? 'Requieren seguimiento' : 'Próximas a cumplir'}
                    color={summary.promesasVencidas > 0 ? red : textPri}
                    modoOscuro={modoOscuro}
                    icon={FiAlertTriangle}
                  />
                </div>
              </section>
            )}

            {hasProfesional && hub?.clientRanking?.length > 0 && (
              <CobranzaClientRanking
                clients={hub.clientRanking}
                modoOscuro={modoOscuro}
                box={box}
                divide={divide}
                textPri={textPri}
                textMuted={textMuted}
                onSelectClient={(cliente) => setSearch(cliente)}
              />
            )}

            {/* Tabla principal */}
            <section className={`order-2 rounded-2xl border overflow-hidden ring-1 lg:order-1 ${
              modoOscuro ? 'ring-[#1F80FF]/30' : 'ring-[#1F80FF]/20'
            } ${box}`}>
                  <div className={`px-4 py-3 border-b space-y-3 ${divide}`}>
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div>
                        <h2 className={`text-sm font-semibold ${textPri}`}>Comprobantes pendientes</h2>
                        <p className={`text-xs ${textMuted}`}>
                          {filteredRows.length} de {hub?.rows?.length || 0} mostrados
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-2">
                      <div className="relative flex-1">
                        <FiSearch className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${textMuted}`} />
                        <input
                          type="search"
                          value={search}
                          onChange={(e) => setSearch(e.target.value)}
                          placeholder="Buscar por cliente, N° o email…"
                          className={`w-full min-h-[44px] pl-9 pr-3 py-2.5 rounded-xl border text-sm ${inputClass}`}
                        />
                      </div>
                      <select
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value)}
                        className={`min-h-[44px] rounded-xl border px-3 py-2.5 text-sm ${inputClass}`}
                      >
                        <option value="prioridad">Orden: prioridad</option>
                        <option value="saldo">Orden: mayor saldo</option>
                        <option value="cliente">Orden: cliente A–Z</option>
                        {hasProfesional && <option value="score">Orden: score bajo</option>}
                      </select>
                    </div>
                    <div className="-mx-4 overflow-x-auto px-4 sm:mx-0 sm:px-0">
                      <div className="flex min-w-max gap-1.5 pb-1 sm:min-w-0 sm:flex-wrap">
                        {FILTER_OPTIONS.map((opt) => (
                          <button
                            key={opt.id}
                            type="button"
                            onClick={() => setAgingFilter(opt.id)}
                            className={`shrink-0 px-3 py-2 rounded-full text-xs font-medium border transition-colors ${
                              agingFilter === opt.id
                                ? modoOscuro ? 'border-[#1F80FF] bg-[#1F80FF]/20 text-[#5CA3FF]' : 'border-[#1F80FF] bg-[#1F80FF]/10 text-[#004AAD]'
                                : modoOscuro ? 'border-gray-700 text-gray-400 hover:bg-gray-800' : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                            }`}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  {!filteredRows.length ? (
                    <p className={`p-8 text-sm text-center ${textMuted}`}>
                      No hay comprobantes con ese filtro.
                      <button type="button" onClick={() => { setAgingFilter('todas'); setSearch(''); }} className="block mx-auto mt-2 text-[#1F80FF] hover:underline text-xs font-semibold">
                        Limpiar filtros
                      </button>
                    </p>
                  ) : (
                    <>
                      {/* Mobile cards */}
                      <div className={`lg:hidden divide-y ${divide}`}>
                        {filteredRows.map((row) => (
                          <InvoiceCard
                            key={row.id}
                            row={row}
                            hasProfesional={hasProfesional}
                            hasCobranzasEssential={hasCobranzasEssential}
                            actionId={actionId}
                            modoOscuro={modoOscuro}
                            textPri={textPri}
                            textMuted={textMuted}
                            divide={divide}
                            onRecordatorio={handleRecordatorio}
                            onPaymentLink={handlePaymentLink}
                            whatsappOptions={whatsappOptions}
                            onOpenGestiones={setGestionRow}
                            onLogGestion={handleAddGestion}
                            readOnly={readOnly}
                          />
                        ))}
                      </div>

                      {/* Desktop table */}
                      <div className="hidden lg:block overflow-x-auto">
                        <table className="w-full text-left text-sm">
                          <thead className={modoOscuro ? 'bg-gray-800/80 text-gray-400' : 'bg-gray-50 text-gray-500'}>
                            <tr>
                              <th className="py-3 pl-4 pr-2 font-semibold">Comprobante</th>
                              <th className="py-3 px-2 font-semibold">Cliente</th>
                              <th className="py-3 px-2 font-semibold text-right">Saldo</th>
                              <th className="py-3 px-2 font-semibold">Aging</th>
                              {hasProfesional && <th className="py-3 px-2 font-semibold">Score</th>}
                              {hasCobranzasEssential && <th className="py-3 px-2 font-semibold">Gestiones</th>}
                              <th className="py-3 pr-4 pl-2 font-semibold text-right">Acciones</th>
                            </tr>
                          </thead>
                          <tbody className={`divide-y ${divide}`}>
                            {filteredRows.map((row) => {
                              const facturaStub = {
                                numero: row.numero,
                                razonSocial: row.cliente,
                                total: row.total,
                                moneda: row.moneda,
                                vencimiento: row.vencimiento,
                                fecha: row.fecha,
                                telefono: row.telefono,
                                pagos: row.pagado > 0 ? [{ monto: row.pagado }] : [],
                              };
                              const aging = agingLabel(facturaStub);
                              const partial = row.pagado > 0 && row.saldo > 0.009;
                              return (
                                <tr key={row.id} className={modoOscuro ? 'hover:bg-gray-800/40' : 'hover:bg-gray-50/80'}>
                                  <td className="py-3.5 pl-4 pr-2">
                                    <div className="flex items-center gap-2">
                                      <span className={`font-semibold ${textPri}`}>N°{row.numero}</span>
                                      {partial && (
                                        <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${modoOscuro ? 'bg-sky-900/40 text-sky-200' : 'bg-sky-50 text-sky-800'}`}>
                                          Parcial
                                        </span>
                                      )}
                                    </div>
                                    <div className={`text-xs ${textMuted}`}>{formatFecha(row.vencimiento || row.fecha)}</div>
                                  </td>
                                  <td className="py-3.5 px-2">
                                    <div className={`font-medium ${textPri}`}>{row.cliente}</div>
                                    <div className={`text-xs truncate max-w-[200px] ${textMuted}`}>{row.email || row.telefono || '—'}</div>
                                  </td>
                                  <td className="py-3.5 px-2 text-right">
                                    <div className={`font-bold tabular-nums ${textPri}`}>
                                      {formatMoney(row.saldo, row.moneda || 'ARS')}
                                    </div>
                                    {partial && (
                                      <div className={`text-[11px] ${textMuted}`}>
                                        Pagado {formatMoney(row.pagado, row.moneda || 'ARS')}
                                      </div>
                                    )}
                                  </td>
                                  <td className="py-3.5 px-2">
                                    {aging && (
                                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${agingTone(facturaStub, modoOscuro)}`}>
                                        {aging}
                                      </span>
                                    )}
                                  </td>
                                  {hasProfesional && (
                                    <td className="py-3.5 px-2">
                                      {row.score != null ? (
                                        <span className={`inline-flex flex-col items-center px-2 py-1 rounded-lg text-[10px] font-bold ${scoreBadgeClasses(scoreTone(row.score), modoOscuro)}`}>
                                          <span>{row.score}</span>
                                          {row.scoreLabel && <span className="font-medium opacity-80">{row.scoreLabel}</span>}
                                        </span>
                                      ) : (
                                        <span className={textMuted}>—</span>
                                      )}
                                    </td>
                                  )}
                                  {hasCobranzasEssential && (
                                    <td className="py-3.5 px-2">
                                      {row.gestionesCount > 0 ? (
                                        <button
                                          type="button"
                                          onClick={() => setGestionRow(row)}
                                          className={`text-xs font-semibold hover:underline ${modoOscuro ? 'text-[#5CA3FF]' : 'text-[#1F80FF]'}`}
                                        >
                                          {row.gestionesCount} · {formatFecha(row.lastGestion?.fecha)}
                                        </button>
                                      ) : !readOnly ? (
                                        <button
                                          type="button"
                                          onClick={() => setGestionRow(row)}
                                          className={`text-xs ${textMuted} hover:underline`}
                                        >
                                          Registrar
                                        </button>
                                      ) : (
                                        <span className={`text-xs ${textMuted}`}>—</span>
                                      )}
                                    </td>
                                  )}
                                  <td className="py-3.5 pr-4 pl-2">
                                    <InvoiceRowActions
                                      row={row}
                                      hasProfesional={hasProfesional}
                                      hasCobranzasEssential={hasCobranzasEssential}
                                      actionId={actionId}
                                      modoOscuro={modoOscuro}
                                      onRecordatorio={handleRecordatorio}
                                      onPaymentLink={handlePaymentLink}
                                      whatsappOptions={whatsappOptions}
                                      onOpenGestiones={setGestionRow}
                                      onLogGestion={handleAddGestion}
                                      readOnly={readOnly}
                                    />
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}
            </section>

            <div className="order-3 grid grid-cols-1 gap-4 sm:gap-5 lg:grid-cols-2 xl:grid-cols-3">
                {/* Promesas */}
                {hub?.promesas?.length > 0 && (
                  <section className={`rounded-2xl border overflow-hidden ${box}`}>
                    <div className={`px-4 py-3 border-b ${divide}`}>
                      <h2 className={`text-sm font-semibold ${textPri}`}>Promesas de cobro</h2>
                      <p className={`text-xs mt-0.5 ${textMuted}`}>
                        {promesasVencidas.length > 0
                          ? `${promesasVencidas.length} vencida(s)`
                          : 'Seguimiento activo'}
                      </p>
                    </div>
                    <ul className={`divide-y max-h-80 overflow-y-auto ${divide}`}>
                      {hub.promesas.slice(0, 10).map((p) => (
                        <li key={`${p.invoiceId}-${p.gestionId}`} className="px-4 py-3">
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0">
                              <div className={`text-sm font-semibold truncate ${textPri}`}>{p.cliente}</div>
                              <div className={`text-xs ${textMuted}`}>N°{p.numero}</div>
                            </div>
                            <span className={`shrink-0 text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${
                              p.vencida
                                ? modoOscuro ? 'bg-red-900/40 text-red-200' : 'bg-red-50 text-red-700'
                                : modoOscuro ? 'bg-amber-900/40 text-amber-200' : 'bg-amber-50 text-amber-800'
                            }`}>
                              {p.vencida ? 'Vencida' : 'Activa'}
                            </span>
                          </div>
                          <div className={`text-xs mt-1.5 flex items-center gap-1 ${p.vencida ? red : textMuted}`}>
                            <FiClock className="w-3 h-3" />
                            Prometió: {formatFecha(p.promesaFecha)}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </section>
                )}

                {/* Datos de cobro */}
                {hasProfesional && prefs && (prefs.paymentAlias || prefs.paymentCbu || prefs.paymentLink) && (
                  <section className={`rounded-2xl border p-4 ${box}`}>
                    <h2 className={`text-sm font-semibold mb-3 ${textPri}`}>Datos para cobrar</h2>
                    {prefs.paymentAccountConfirmed && (
                      <p className={`text-[11px] font-semibold mb-2 ${modoOscuro ? 'text-green-300' : 'text-green-700'}`}>
                        Titular verificado en Cuenta
                      </p>
                    )}
                    <dl className="space-y-2 text-sm">
                      {prefs.paymentHolderName && (
                        <div>
                          <dt className={`text-xs ${textMuted}`}>Titular</dt>
                          <dd className={`font-medium ${textPri}`}>{prefs.paymentHolderName}</dd>
                        </div>
                      )}
                      {prefs.paymentAlias && (
                        <div>
                          <dt className={`text-xs ${textMuted}`}>Alias</dt>
                          <dd className={`font-medium ${textPri}`}>{prefs.paymentAlias}</dd>
                        </div>
                      )}
                      {prefs.paymentCbu && (
                        <div>
                          <dt className={`text-xs ${textMuted}`}>CBU</dt>
                          <dd className={`font-medium tabular-nums ${textPri}`}>{prefs.paymentCbu}</dd>
                        </div>
                      )}
                    </dl>
                    <Link
                      to="/cuenta?tab=cobro"
                      className="inline-flex items-center gap-1 mt-3 text-xs font-semibold text-[#1F80FF] hover:underline"
                    >
                      <FiSettings className="w-3 h-3" />
                      Editar en Cuenta
                    </Link>
                  </section>
                )}

                {!hasProfesional && (
                  <PlanUpgradeCard
                    title="Desbloqueá cobranzas pro"
                    description="Acciones avanzadas para cobrar más rápido y con menos fricción."
                    bullets={[
                      'Link Mercado Pago por comprobante',
                      'Recordatorios con PDF adjunto',
                      'Secuencias automáticas +3/+7/+15',
                    ]}
                    planLabel="Profesional"
                    modoOscuro={modoOscuro}
                    box={box}
                    textPri={textPri}
                    textMuted={textMuted}
                  />
                )}
            </div>
          </div>
        )}
      </div>

      {hasCobranzasEssential && gestionRow && (
        <CobranzaGestionesModal
          row={gestionRow}
          modoOscuro={modoOscuro}
          saving={savingGestion}
          readOnly={readOnly}
          onClose={() => setGestionRow(null)}
          onSave={(payload) => handleAddGestion(gestionRow.id, payload)}
        />
      )}
    </div>
  );
}
